import React from 'react';
import { Backdrop, Box, Stack, Typography, Zoom, styled, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  BarChartRounded,
  AddRounded,
  HomeRounded,
  PeopleRounded,
  PointOfSaleRounded,
  MoreHorizRounded,
  ConfirmationNumberRounded,
  QrCode2Rounded,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackNavigationIntent } from '../lib/app-telemetry';
import { getPrimaryNavItems, isNavItemActive } from './navigation/nav-config.jsx';
import { useOrganization } from '../contexts/OrganizationContext';
import LockedFeatureModal from './shared/LockedFeatureModal.jsx';

const StyledPaper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1145,
  paddingBottom: 'env(safe-area-inset-bottom)',
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  height: 'calc(69px + env(safe-area-inset-bottom))',
  boxSizing: 'border-box',
  // Fix for mobile browser address bar jumping
  transform: 'translateZ(0)',
  willChange: 'bottom, transform',
  backfaceVisibility: 'hidden',
  '& .nav-background': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '69px',
    zIndex: 0,
    pointerEvents: 'none',
    filter: theme.palette.mode === 'light'
      ? `drop-shadow(0px -8px 24px ${alpha(theme.palette.primary.main, 0.22)})`
      : 'drop-shadow(0px -8px 24px rgba(0, 0, 0, 0.5))',
  },
  '& .safe-area-fill': {
    position: 'absolute',
    top: '69px',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
    backgroundColor: theme.palette.background.paper,
  },
}));

const NavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ active, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  zIndex: 1001,
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',
  outline: 'none',
  '& .MuiSvgIcon-root': {
    fontSize: 23,
  },
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const ActionButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ active, theme }) => ({

  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.main,
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '-20px',
  boxShadow: active
    ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.18)}`
    : theme.palette.mode === 'light'
      ? `0 16px 40px ${alpha(theme.palette.primary.main, 0.28)}`
      : '0 8px 32px rgba(0, 0, 0, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.18 : 0.1)}`,
  zIndex: 1005,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',
  outline: 'none',
  '&:active': {
    transform: 'scale(0.9) translateY(5px)',
  },
}));

const ActionMenuContainer = styled(Box)(() => ({
  position: 'fixed',
  bottom: 'calc(90px + env(safe-area-inset-bottom))',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '14px',
  zIndex: 1150,
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
}));

const MenuButton = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.background.paper
    : alpha(theme.palette.background.paper, 0.96),
  width: '52px',
  height: '52px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: theme.palette.mode === 'light'
    ? `0 10px 24px ${alpha(theme.palette.primary.main, 0.22)}`
    : `0 8px 24px ${alpha('#000000', 0.32)}`,
  border: `1px solid ${theme.palette.mode === 'light'
    ? alpha(theme.palette.primary.main, 0.12)
    : alpha(theme.palette.common.white, 0.16)}`,
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',
  outline: 'none',
  '&:active': {
    transform: 'scale(0.92)',
  },
}));

const MenuLabel = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light'
    ? alpha(theme.palette.background.paper, 0.92)
    : alpha(theme.palette.background.paper, 0.96),
  color: theme.palette.text.primary,
  padding: '6px 13px',
  borderRadius: '12px',
  fontSize: '0.82rem',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  backdropFilter: 'blur(8px)',
  border: `1px solid ${theme.palette.mode === 'light'
    ? theme.palette.divider
    : alpha(theme.palette.common.white, 0.16)}`,
  whiteSpace: 'nowrap',
  boxShadow: theme.palette.mode === 'light'
    ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.16)}`
    : `0 6px 16px ${alpha('#000000', 0.32)}`,
}));

const MobileNavbar = ({ session }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [lockedFeatureModal, setLockedFeatureModal] = React.useState({ open: false, title: "" });
  const { hasFeature } = useOrganization() || {};
  const hasMemberships = hasFeature ? hasFeature('memberships') : true;
  const hasQrIntake = hasFeature ? hasFeature('qr_intake') : true;

  const handleNav = React.useCallback(({ path, label }) => {
    setOpen(false);
    trackNavigationIntent({
      source: 'mobile_bottom_nav',
      trigger: 'bottom_tab',
      from: location.pathname,
      to: path,
      label,
    });
    navigate(path);
  }, [location.pathname, navigate]);

  const fabActions = [
    {
      icon: <PointOfSaleRounded sx={{ fontSize: 26, color: '#10b981' }} />,
      label: 'Cargar venta',
      path: '/sales/new',
    },
    hasMemberships && {
      icon: <ConfirmationNumberRounded sx={{ fontSize: 26, color: '#f59e0b' }} />,
      label: 'Consumir membresía',
      path: '/memberships/consume',
    },
    {
      icon: <QrCode2Rounded sx={{ fontSize: 26, color: '#38bdf8' }} />,
      label: 'QR carga cliente',
      path: '/customers/intake-qr',
      locked: !hasQrIntake,
    },
  ].filter(Boolean);
  const primaryItems = getPrimaryNavItems(session);
  const homeItem = primaryItems.find((item) => item.path === '/home') ?? primaryItems[0];
  const statsItem = primaryItems.find((item) => item.path === '/stats');
  const customersItem = primaryItems.find((item) => item.path === '/customers');
  const moreItem = primaryItems.find((item) => item.path === '/management');

  return (
    <>
      <Backdrop
        open={open}
        onClick={() => setOpen(false)}
        sx={{
          zIndex: 1140,
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(4px)',
        }}
      />
      <ActionMenuContainer>
        {fabActions.map((action, index) => (
          <Zoom
            key={action.path}
            in={open}
            style={{ transitionDelay: open ? `${index * 45}ms` : '0ms' }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                width: 222,
                opacity: action.locked ? 0.6 : 1,
                filter: action.locked ? "grayscale(100%)" : "none"
              }}>
              <MenuLabel variant="caption">{action.label}</MenuLabel>
              <MenuButton 
                onClick={action.locked ? () => setLockedFeatureModal({ open: true, title: action.label }) : () => handleNav({ path: action.path, label: action.label })}
              >
                {action.icon}
              </MenuButton>
            </Stack>
          </Zoom>
        ))}
      </ActionMenuContainer>
      <StyledPaper>
        <Box className="safe-area-fill" />
        <Box className="nav-background">
          <svg width="100%" height="100%" viewBox="0 0 400 65" preserveAspectRatio="none">
            <path
              d="M0,10 Q0,0 10,0 L140,0 C170,0 170,45 200,45 C230,45 230,0 260,0 L390,0 Q400,0 400,10 V65 H0 Z"
              fill={theme.palette.background.paper}
            />
          </svg>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around', alignItems: 'center', zIndex: 1001 }}>
          {session?.isActive !== false ? (
            <>
              <NavItem
                active={isNavItemActive(location.pathname, homeItem)}
                onClick={() => handleNav({ path: homeItem.path, label: homeItem.label })}
              >
                <HomeRounded />
                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.72rem', letterSpacing: '-0.01em' }}>
                  {homeItem.label}
                </Typography>
              </NavItem>

              <NavItem
                active={isNavItemActive(location.pathname, statsItem)}
                onClick={() => handleNav({ path: statsItem.path, label: statsItem.label })}
              >
                <BarChartRounded />
                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.72rem', letterSpacing: '-0.01em' }}>
                  {statsItem.label}
                </Typography>
              </NavItem>
            </>
          ) : (
            <NavItem
              active={isNavItemActive(location.pathname, primaryItems[0])}
              onClick={() => handleNav({ path: primaryItems[0].path, label: primaryItems[0].label })}
            >
              <PointOfSaleRounded />
              <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 600 }}>{primaryItems[0].label}</Typography>
            </NavItem>
          )}
        </Box>

        {session?.isActive !== false && (
          <Box sx={{ width: '70px', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1145 }}>
            <ActionButton
              onClick={() => setOpen((value) => !value)}
              active={open}
            >
              <AddRounded
                sx={{
                  fontSize: '36px',
                  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: open ? 'rotate(135deg)' : 'rotate(0deg)',
                }}
              />
            </ActionButton>
          </Box>
        )}

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around', alignItems: 'center', zIndex: 1001 }}>
          {session?.isActive !== false ? (
            <>
              <NavItem
                active={isNavItemActive(location.pathname, customersItem)}
                onClick={() => handleNav({ path: customersItem.path, label: customersItem.label })}
              >
                <PeopleRounded />
                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.72rem', letterSpacing: '-0.01em' }}>{customersItem.label}</Typography>
              </NavItem>

              <NavItem
                active={isNavItemActive(location.pathname, moreItem)}
                onClick={() => handleNav({ path: moreItem.path, label: moreItem.label })}
              >
                <MoreHorizRounded />
                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.72rem', letterSpacing: '-0.01em' }}>{moreItem.label}</Typography>
              </NavItem>
            </>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
        </Box>
      </StyledPaper>

      <LockedFeatureModal
        open={lockedFeatureModal.open}
        title={lockedFeatureModal.title}
        onClose={() => setLockedFeatureModal({ open: false, title: "" })}
      />
    </>
  );
};

export default MobileNavbar;
